import {useMutation, useQuery} from "@apollo/client";
import {useState} from "react";
import {useCookies} from "react-cookie";
import {redirect, useLocation, useNavigate} from "react-router";

import {useCheckoutContext, useCurrentUserContext} from "@/components/context/hooks.ts";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {trackPurchase} from "@/components/trackers/GoogleTagEvents.ts";
import {GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";
import {
  CalculatedBillingDataResponse,
  GQL_CALCULATE_BILLING_DATA,
  GQL_CHECKOUT_CONTEXT,
  GQL_UPDATE_SUBSCRIPTION,
  UpdateSubscriptionResponse,
} from "@/lib/queries/billing.ts";
import {AddedValue, BillingPeriod, CalculatedBillingData, Plan} from "@/lib/types/billing.ts";
import {QueryError, ResponseStatus} from "@/lib/types/common.ts";
import {PurchaseSummary} from "@/pages/billing/checkout/PurchaseSummary.tsx";
import {BillingPeriodPicker} from "@/pages/billing/components/BillingPeriodPicker.tsx";
import {PaymentMethod} from "@/pages/billing/payment-method/PaymentMethod.tsx";
import {AddedValuesWarning} from "@/pages/billing/subscription/components/AddedValuesWarning.tsx";

export const Checkout = () => {
  const location = useLocation();
  const navigateFn = useNavigate();
  if (!location.state) {
    redirect("/billing/subscribe");
  }
  const {user} = useCurrentUserContext();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod | undefined>(location.state?.billingPeriod);
  const [plan] = useState<Plan | undefined>(location.state?.plan);
  const addedValues: AddedValue[] = location.state?.addedValues || [];
  const [promoCode, setPromoCode] = useState<string>("");
  const [updateSubscription] = useMutation<UpdateSubscriptionResponse>(GQL_UPDATE_SUBSCRIPTION, {
    refetchQueries: [GQL_USER_CONTEXT, GQL_CHECKOUT_CONTEXT],
  });
  const [updateErrors, setUpdateErrors] = useState<string[]>([]);
  const {data} = useQuery<CalculatedBillingDataResponse>(GQL_CALCULATE_BILLING_DATA, {
    variables: {
      billingPeriod,
      plan,
      addedItems: addedValues,
      promoCode,
      skipApplicabilityCheck: false,
    },
  });
  // eslint-disable-next-line no-unused-vars
  const [_cookies, _setCookie, removeCookie] = useCookies(["subscriptionPageNextGen"]);
  const {setRequestInProgress, hasActiveSubscription} = useCheckoutContext();
  // this block is a fail-safe: here we show the error message if it's not shown in the PurchaseSummary
  if ((data?.calculateBillingData as QueryError)?.status === ResponseStatus.ERROR) {
    return <FormErrorAlerts errors={(data?.calculateBillingData as QueryError)?.message?.__all__ || []} />;
  }
  return (
    <>
      {!hasActiveSubscription && (
        <div className="w-100">
          <div className="mx-auto">
            <div className="d-flex flex-row align-items-center justify-content-center">
              <BillingPeriodPicker
                value={billingPeriod}
                onChange={(billingPeriod) => {
                  setPromoCode("");
                  setBillingPeriod(billingPeriod);
                }}
                disabled={!user?.account.free_trial_expires_at}
              />
            </div>
          </div>
        </div>
      )}

      {addedValues.length > 0 && !hasActiveSubscription && (
        <div className="my-4 px-4">
          <AddedValuesWarning />
        </div>
      )}
      <FormErrorAlerts errors={updateErrors} />
      <div className="row mt-4">
        <div className="col-8">
          <PaymentMethod
            subscription={true}
            allowEmpty={false}
            readOnly={!user?.is_primary}
            withButtons={false}
            formId="paymentMethodForm"
            variant="inline"
            onSuccess={async () => {
              // By this time, the payment method is already set, so we only
              // need to submit the checkout request. There still can be a problem
              // with payment provider, or with validation of the checkout order.
              try {
                const result = await updateSubscription({
                  variables: {
                    plan,
                    billingPeriod,
                    addedValues,
                    promoCode,
                  },
                });
                if (result.data?.updateSubscription?.success === true) {
                  removeCookie("subscriptionPageNextGen");
                  const calculatedData = data?.calculateBillingData as CalculatedBillingData;
                  const newSubscriptionPrice = calculatedData?.price_monthly || 0;
                  trackPurchase(newSubscriptionPrice, billingPeriod || "", promoCode, user);
                  navigateFn(`/billing/subscription/done?action=subscription&grmkplan=${newSubscriptionPrice}`);
                } else {
                  setUpdateErrors(result.data?.updateSubscription?.errors?.__all__ || []);
                }
              } catch {
                setUpdateErrors(["An error occurred while updating the subscription."]);
              } finally {
                setRequestInProgress(false);
              }
            }}
          />
        </div>
        <div className="col-4">
          <PurchaseSummary
            calculatedData={data?.calculateBillingData as CalculatedBillingData}
            billingPeriod={billingPeriod}
            addedValues={addedValues}
            plan={plan}
            onPromoCodeSuccess={setPromoCode}
            formId="paymentMethodForm"
            isCustomer={hasActiveSubscription || false}
            isUpgrade={
              !user?.account.plan || (user?.account.plan !== plan && user?.account.billing_period === billingPeriod)
            }
          />
        </div>
      </div>
    </>
  );
};
